<template>
  <div
    class="ndsModal"
    :class="{
      opened: value,
    }"
    @keydown.esc.prevent="handleEsc"
  >
    <transition name="fade">
      <div
        v-if="value"
        class="ndsModal__backdrop"
        @mousedown="handleMouseDown"
      >
        <div
          ref="modal"
          class="ndsModal__container"
          :class="[`-${size}`]"
          data-testid="nds-modal-container"
          :style="{
            height,
            maxHeight: height === 'auto' ? '80%' : 'auto',
          }"
          tabindex="-1"
        >
          <header
            v-if="$slots.title || $slots.description || showCloseButton"
            class="ndsModalHeader"
            :class="[`-${headerBackground}`, { '-borderBottom': borders.includes(NdsModalBorder.HEADER_BOTTOM) }]"
            data-testid="nds-modal-header"
          >
            <div class="ndsModalHeader__left">
              <NdsHeading
                v-if="$slots.title"
                data-testid="nds-modal-title"
                :tag="NdsHeadingTags.H6"
              >
                <slot name="title" />
              </NdsHeading>

              <NdsText
                v-if="$slots.description"
                class="!tw-text-neutral-700"
                data-testid="nds-modal-description"
                :size="NdsTextSizes.L"
              >
                <slot name="description" />
              </NdsText>
            </div>

            <div v-if="showCloseButton">
              <NdsButton
                :color="NdsButtonColor.TERTIARY"
                data-testid="nds-modal-close"
                :icon-left="NdsIconType.CLOSE"
                :size="NdsButtonSize.S"
                @click="handleClose"
              />
            </div>
          </header>

          <div
            v-if="$slots.beforeContent"
            class="ndsModal__beforeContent"
            :class="{
              '-borderTop': borders.includes(NdsModalBorder.BEFORE_CONTENT_TOP),
              '-borderBottom': borders.includes(NdsModalBorder.BEFORE_CONTENT_BOTTOM),
            }"
          >
            <slot name="beforeContent" />
          </div>

          <div
            v-if="$slots.content"
            class="ndsModal__content"
            :class="{
              '-borderTop': borders.includes(NdsModalBorder.CONTENT_TOP),
              '-borderBottom': borders.includes(NdsModalBorder.CONTENT_BOTTOM),
            }"
          >
            <slot name="content" />
          </div>

          <div
            v-if="$slots.afterContent"
            class="ndsModal__afterContent"
            :class="{
              '-borderTop': borders.includes(NdsModalBorder.AFTER_CONTENT_TOP),
              '-borderBottom': borders.includes(NdsModalBorder.AFTER_CONTENT_BOTTOM),
            }"
          >
            <slot name="afterContent" />
          </div>

          <footer
            v-if="$slots.footer"
            class="ndsModal__footer"
            :class="{
              '-borderTop': borders.includes(NdsModalBorder.FOOTER_TOP),
            }"
            data-testid="nds-modal-footer"
          >
            <slot name="footer" />
          </footer>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, type PropType, ref, watch } from 'vue'

import {
  NdsModalBorder,
  NdsModalHeaderBackground,
  NdsModalSize,
} from '@/components/NewDesignSystem/NdsModal/NdsModal.interfaces'
import { NdsButtonColor, NdsButtonSize } from '@/components/NewDesignSystem/NdsButton/NdsButton.interfaces'
import { NdsIconType } from '@/components/NewDesignSystem/NdsIcon/NdsIcon.interfaces'
import { NdsHeadingTags } from '@/components/NewDesignSystem/NdsHeading/NdsHeading.interfaces'
import { NdsTextSizes } from '@/components/NewDesignSystem/NdsText/NdsText.interfaces'

import NdsHeading from '@/components/NewDesignSystem/NdsHeading/NdsHeading.vue'
import NdsButton from '@/components/NewDesignSystem/NdsButton/NdsButton.vue'
import NdsText from '@/components/NewDesignSystem/NdsText/NdsText.vue'

import { rootBus } from '@/contexts/shared/event-bus/RootBus'

const emit = defineEmits<{
  (e: 'input', value: boolean): void
  (e: 'opened'): void
  (e: 'close'): void
  (e: 'closed'): void
}>()

const props = defineProps({
  borders: {
    type: Array as PropType<NdsModalBorder[]>,
    default: () => [NdsModalBorder.FOOTER_TOP],
  },
  closeOnBackdrop: {
    type: Boolean,
    default: true,
  },
  closeOnEsc: {
    type: Boolean,
    default: true,
  },
  headerBackground: {
    type: String as PropType<NdsModalHeaderBackground>,
    default: NdsModalHeaderBackground.WHITE,
  },
  height: {
    type: String,
    default: 'auto',
  },
  id: {
    type: [String, null] as PropType<string | null>,
    default: null,
  },
  showCloseButton: {
    type: Boolean,
    default: true,
  },
  size: {
    type: String as PropType<NdsModalSize>,
    default: NdsModalSize.MEDIUM,
  },
  value: {
    type: Boolean,
    default: false,
  },
})

const modal = ref()
/**
 * @warning
 * The imperative show/hide mechanism (via `id` prop + rootBus events) is intended solely
 * as a migration bridge from `$bvModal.show()` / `$bvModal.hide()`.
 * Do not use this pattern for new features.
 * Consider using `v-model` instead.
 */
const isImperativelyControlled = props.id !== null

const handleMouseDown = (e: MouseEvent) => {
  if (props.closeOnBackdrop && e.target instanceof HTMLElement && e.target.classList.contains('ndsModal__backdrop')) {
    handleClose()
  }
}

const handleEsc = () => {
  if (props.closeOnEsc) {
    handleClose()
  }
}

const handleClose = () => {
  emit('input', false)
  emit('close')
}

watch(
  () => props.value,
  async (newValue) => {
    if (newValue) {
      emit('opened')
      await nextTick()
      modal.value?.focus()
    } else {
      emit('closed')
    }
  }
)

const handleImperativeShow = (eventId: unknown) => {
  if (eventId === props.id) {
    emit('input', true)
  }
}

const handleImperativeHide = (eventId: unknown) => {
  if (eventId === props.id) {
    emit('input', false)
  }
}

onMounted(() => {
  if (props.value) {
    emit('opened')
  }

  if (isImperativelyControlled) {
    rootBus.on('modal:show', handleImperativeShow)
    rootBus.on('modal:hide', handleImperativeHide)
  }
})

onBeforeUnmount(() => {
  if (isImperativelyControlled) {
    rootBus.off('modal:show', handleImperativeShow)
    rootBus.off('modal:hide', handleImperativeHide)
  }
})
</script>

<style lang="postcss" scoped>
.ndsModal {
  &__backdrop {
    background-color: rgba(0, 0, 0, 0.25);
    position: fixed;
    inset: 0;
    z-index: 2666;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__container {
    display: flex;
    flex-direction: column;
    width: 640px;
    background-color: white;
    border-radius: var(--spacing-2);
    box-shadow: var(--box-shadow-e300);

    &:focus {
      outline: none;
    }

    &.-small {
      width: 480px;

      .ndsModal__footer {
        padding: var(--spacing-2);
      }
      .ndsModalHeader {
        padding: var(--spacing-2);
      }
    }

    &.-big {
      width: 800px;
    }

    &.-xlarge {
      width: 1056px;
    }

    .ndsModalHeader {
      display: flex;
      gap: var(--spacing-1);
      padding: var(--spacing-3);
      border-radius: var(--spacing-2) var(--spacing-2) 0 0;

      &__left {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-2);
        flex-grow: 1;
      }

      &.-white {
        background-color: white;
      }

      &.-warning {
        background-color: var(--warning-50);
      }

      &.-danger {
        background-color: var(--danger-100);
      }

      &.-success {
        background-color: var(--success-50);
      }
    }
  }

  &__content {
    flex-grow: 1;
    position: relative;
    overflow: hidden;
    overflow-y: auto;
  }

  &__footer {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: var(--spacing-1);
    padding: var(--spacing-3);
  }
}

.-borderTop {
  border-top: 1px solid;
  border-color: var(--neutral-300);
}

.-borderBottom {
  border-bottom: 1px solid;
  border-color: var(--neutral-300);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
